import React, { createContext, useContext, useState, ReactNode } from "react";
import { DEFAULT_LANGUAGE } from "@/constants";

interface GeneralContextType {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  theme: "light" | "dark";
  toggleTheme: () => void;
  language: string;
  setLanguage: (lang: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const GeneralContext = createContext<GeneralContextType | undefined>(undefined);

interface GeneralProviderProps {
  children: ReactNode;
}

export const GeneralProvider: React.FC<GeneralProviderProps> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  // Initialize language from localStorage (consistent with i18n)
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || DEFAULT_LANGUAGE
  );
  const [activeTab, setActiveTab] = useState("Dashboard");

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Sync language changes to localStorage
  const handleSetLanguage = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const value: GeneralContextType = {
    isLoading,
    setIsLoading,
    theme,
    toggleTheme,
    language,
    setLanguage: handleSetLanguage,
    activeTab,
    setActiveTab,
  };

  return (
    <GeneralContext.Provider value={value}>{children}</GeneralContext.Provider>
  );
};

export const useGeneral = (): GeneralContextType => {
  const context = useContext(GeneralContext);
  if (context === undefined) {
    throw new Error("useGeneral must be used within a GeneralProvider");
  }
  return context;
};
